<!-- NoteFilter.vue -->
<script setup>
import { onMounted, onBeforeUnmount, watch, ref, computed } from "vue";
import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";
import SelectNoteUser from "./SelectNoteUser.vue";
import SelectNoteGroup from "./SelectNoteGroup.vue";
import SelectFieldFilter from "./SelectFieldFilter.vue";
import SelectPeriodFilter from "./SelectPeriodFilter.vue";
import SelectSortOrderFilter from "./SelectSortOrderFilter.vue";
import SelectObservationTypeFilter from "./SelectObservationTypeFilter.vue";
import { useAnchoredPanelScope } from "@/composables/useAnchoredPanel";

const noteStore = useNoteStore();
const activityStore = useActivityStore();
const userStore = useUserStore();

// Os sub-modais abrem fora do fluxo (teleportados para o body, ver
// AnchoredPanel): o menu tem overflow e os recortaria. Por isso, no DOM, eles
// não estão dentro do dropdownRef - o escopo diz se um clique caiu num deles.
const anchoredScope = useAnchoredPanelScope();

const showDropdown       = ref(false);
const dropdownRef        = ref(null);
const showUserDropdown   = ref(false);
const showGroupDropdown  = ref(false);
const showTitleDropdown  = ref(false); // >>> NOVO
const showBodyDropdown   = ref(false);
const showTagDropdown    = ref(false);
const showSourceDropdown = ref(false);
const showPeriodDropdown = ref(false);
const showObsTypeDropdown = ref(false); // >>> NOVO: tipo de observação
const showSortDropdown   = ref(false);  // >>> NOVO: ordenação

function closeAllSubModals() {
  showUserDropdown.value   = false;
  showGroupDropdown.value  = false;
  showTitleDropdown.value  = false; // >>> NOVO
  showBodyDropdown.value   = false;
  showTagDropdown.value    = false;
  showSourceDropdown.value = false;
  showPeriodDropdown.value = false;
  showObsTypeDropdown.value = false; // >>> NOVO
  showSortDropdown.value = false;    // >>> NOVO
}

function toggleDropdown() {
  showDropdown.value = !showDropdown.value;
  if (!showDropdown.value) closeAllSubModals();
}

// Dispara busca paginada resetando para página 1
function triggerSearch() {
  noteStore.fetchNotesByActivityIdPaged(
    activityStore.idActivity,
    1,
    noteStore.pageSize,
    noteStore.searchTerm
  );
  noteStore.currentPage = 1;
  noteStore.shouldForcePageOne = true;
}

// ── SEÇÃO BUSCA ──────────────────────────────────────────
// Filtros de campo: clique alterna entre abrir modal (inativo) ou limpar (ativo)
function handleTitleClick(e) { // >>> NOVO
  e.stopPropagation();
  if (noteStore.filterByTitle.trim() !== "") {
    noteStore.filterByTitle = "";
    triggerSearch();
  } else {
    closeAllSubModals();
    showTitleDropdown.value = true;
  }
}

function handleBodyClick(e) {
  e.stopPropagation();
  if (noteStore.filterByBody.trim() !== "") {
    noteStore.filterByBody = "";
    triggerSearch();
  } else {
    closeAllSubModals();
    showBodyDropdown.value = true;
  }
}

function handleTagClick(e) {
  e.stopPropagation();
  if (noteStore.filterByTag.trim() !== "") {
    noteStore.filterByTag = "";
    triggerSearch();
  } else {
    closeAllSubModals();
    showTagDropdown.value = true;
  }
}

function handleSourceClick(e) {
  e.stopPropagation();
  if (noteStore.filterBySource.trim() !== "") {
    noteStore.filterBySource = "";
    triggerSearch();
  } else {
    closeAllSubModals();
    showSourceDropdown.value = true;
  }
}

// Chamado pelo emit @apply dos SelectFieldFilter
function applyFieldFilter() {
  closeAllSubModals();
  triggerSearch();
}

// ── SEÇÃO SINALIZAÇÃO ────────────────────────────────────
function toggleIncludeOwn() {
  noteStore.filterIncludeOwn = !noteStore.filterIncludeOwn;
  clearUserFilterIfOverridden();
  triggerSearch();
}

function toggleIncludeComments() {
  noteStore.filterIncludeComments = !noteStore.filterIncludeComments;
  clearUserFilterIfOverridden();
  triggerSearch();
}

// "Minhas notas"/"Notas de outros" são mais gerais e sobrepõem "Por usuários".
// O watcher abaixo já faz isso, mas de forma ASSÍNCRONA (flush 'pre'), enquanto
// triggerSearch() roda sincronamente logo em seguida — o fetch sairia com o
// filtro por usuário ainda ativo e a lista voltaria com notas que deveriam sair
// (ex.: a própria nota ao marcar "Notas de outros"). Zeramos aqui, síncrono,
// para o fetch já usar o estado correto.
function clearUserFilterIfOverridden() {
  if (
    noteStore.filterIncludeSelectedUsers &&
    (noteStore.filterIncludeOwn || noteStore.filterIncludeComments)
  ) {
    noteStore.filterIncludeSelectedUsers = false;
    noteStore.selectedUserIdsForFilter = [];
  }
}

// >>> NOVO: filtro por tipo de observação (+ busca local na observação)
// Clique na linha: se o sub-modal está aberto, fecha; senão abre o modal já
// refletindo os parâmetros atuais (tipos marcados / termo de busca), que vêm
// do store via v-model — a limpeza é feita dentro do próprio modal.
function handleObsTypeRowClick(e) {
  e.stopPropagation();
  if (showObsTypeDropdown.value) {
    showObsTypeDropdown.value = false;
    return;
  }
  closeAllSubModals();
  showObsTypeDropdown.value = true;
}

// Marcar/desmarcar um tipo ou digitar na busca rebusca sem fechar o sub-modal.
function applyObsTypeFilter() {
  triggerSearch();
}

// Filtro ativo quando há tipos marcados, estado de leitura escolhido OU termo
// de busca na observação.
const obsTypeActive = computed(
  () =>
    noteStore.selectedObservationTypes.length > 0 ||
    noteStore.observationReadState !== "" ||
    noteStore.observationSearchTerm.trim() !== "",
);

// Badge da linha: nº de checkboxes marcados no sub-modal (tipo + leitura).
// O termo de busca não entra — ele já se reflete no checkmark da linha.
const obsCheckedCount = computed(
  () =>
    noteStore.selectedObservationTypes.length +
    (noteStore.observationReadState !== "" ? 1 : 0),
);

// O filtro por tipo de observação só faz sentido para o dono da atividade
// (quem lida com observações/correções). A linha só aparece nesse caso.
const isActivityOwner = computed(() => {
  const ownerId = activityStore.selectedActivity?.createdByUserId;
  const uid = userStore.currentUser?.userId;
  return !!ownerId && !!uid && ownerId === uid;
});

// Se o contexto muda para uma atividade de outro dono (ou já entra assim) e
// havia um filtro de observação ativo, limpa-o para não filtrar de forma oculta
// — a linha some, então não haveria UI para removê-lo. Observa os ids brutos
// (não `isActivityOwner`) para só agir quando atividade e usuário já resolveram.
watch(
  () => [
    activityStore.selectedActivity?.createdByUserId,
    userStore.currentUser?.userId,
  ],
  ([ownerId, uid]) => {
    if (!ownerId || !uid) return;   // contexto ainda não carregado
    if (ownerId === uid) return;    // é o dono: mantém o filtro
    if (obsTypeActive.value) {
      noteStore.selectedObservationTypes = [];
      noteStore.observationSearchTerm = "";
      noteStore.observationReadState = "";
      showObsTypeDropdown.value = false;
      triggerSearch();
    }
  },
  { immediate: true },
);

// >>> NOVO: alterna o filtro "Notas avulsas"
function toggleOnlyStandalone() {
  noteStore.filterOnlyStandalone = !noteStore.filterOnlyStandalone;
  triggerSearch();
}

// ── SEÇÃO VISIBILIDADE ───────────────────────────────────
// Por usuário:
// - Se o modal está aberto mas não aplicado: apenas fecha
// - Se já está ativo (checkmark visível): limpa e rebusca
// - Se inativo: abre modal
function handleUserRowClick(e) {
  e.stopPropagation();
  if (showUserDropdown.value) {
    showUserDropdown.value = false;
    return;
  }
  closeAllSubModals();
  showUserDropdown.value = true;
}

// Chamado pelo emit @apply-selection do SelectNoteUser
function applyUserFilter(selectionApplied) {
  // A regra (incl. "filtro por usuário desmarca Minhas notas/Notas de outros")
  // vive no store para ser compartilhada com o NoteCreatorsStack.
  noteStore.applyUserSelectionFilter({ selectionApplied });
  closeAllSubModals();
  noteStore.setNoteFilters({
    includeOwn: noteStore.filterIncludeOwn,
    comments: noteStore.filterIncludeComments,
    sortBy: noteStore.filterSortOrder,
    includeSelectedUsers: noteStore.filterIncludeSelectedUsers,
    includeSelectedGroups: noteStore.filterIncludeSelectedGroups,
  });
}

// Por grupo — mesma lógica
function handleGroupRowClick(e) {
  e.stopPropagation();
  if (showGroupDropdown.value) {
    showGroupDropdown.value = false;
    return;
  }
  if (noteStore.filterIncludeSelectedGroups) {
    noteStore.filterIncludeSelectedGroups = false;
    noteStore.selectedGroupIdsForFilter = [];
    triggerSearch();
  } else {
    closeAllSubModals();
    showGroupDropdown.value = true;
  }
}

function applyGroupFilter(selectionApplied) {
  const hasGroups = noteStore.selectedGroupIdsForFilter.length > 0;
  noteStore.filterIncludeSelectedGroups = selectionApplied && hasGroups;
  if (!noteStore.filterIncludeSelectedGroups) {
    noteStore.clearGroupSelection();
  } else {
    // Filtro por grupo é mais específico: desmarca Minhas notas/Notas de outros
    noteStore.filterIncludeOwn = false;
    noteStore.filterIncludeComments = false;
  }
  closeAllSubModals();
  noteStore.setNoteFilters({
    includeOwn: noteStore.filterIncludeOwn,
    comments: noteStore.filterIncludeComments,
    sortBy: noteStore.filterSortOrder,
    includeSelectedUsers: noteStore.filterIncludeSelectedUsers,
    includeSelectedGroups: noteStore.filterIncludeSelectedGroups,
  });
}

// ── SEÇÃO ORDENAÇÃO ──────────────────────────────────────
// A ordem padrão é a do texto — a mesma dos expoentes que o leitor mostra.
const DEFAULT_SORT_ORDER = "textOrder";

const SORT_ORDER_LABELS = {
  textOrder: "Ordem do texto",
  newest: "Mais recentes primeiro",
  oldest: "Mais antigas primeiro",
};

const sortOrderLabel = computed(
  () => SORT_ORDER_LABELS[noteStore.filterSortOrder] ?? SORT_ORDER_LABELS.textOrder
);

// Ordenação não é filtro: não faz sentido "limpar" para nenhuma ordem. Por isso
// a linha só abre e fecha o mini-modal, diferente das linhas de período e de
// grupo, onde o clique com filtro ativo limpa.
function handleSortRowClick(e) {
  e.stopPropagation();
  if (showSortDropdown.value) {
    showSortDropdown.value = false;
    return;
  }
  closeAllSubModals();
  showSortDropdown.value = true;
}

// A ordem real da paginação é do SERVIDOR: ordenar só no cliente reordenaria
// apenas a página exibida, e a página 2 continuaria sendo "as próximas mais
// recentes". Por isso aplicar refaz a busca em vez de mexer na lista em mãos.
function applySortOrder() {
  closeAllSubModals();
  triggerSearch();
}

// ── SEÇÃO PERÍODO ────────────────────────────────────────
function handlePeriodRowClick(e) {
  e.stopPropagation();
  if (showPeriodDropdown.value) {
    showPeriodDropdown.value = false;
    return;
  }
  if (hasPeriodActive.value) {
    noteStore.filterPeriod = { field: "createdAt", dateFrom: "", dateTo: "" };
    triggerSearch();
  } else {
    closeAllSubModals();
    showPeriodDropdown.value = true;
  }
}

function applyPeriodFilter() {
  closeAllSubModals();
  triggerSearch();
}

// ── LIMPAR TUDO ──────────────────────────────────────────
function clearAllFilters() {
  noteStore.resetNoteFiltersToDefault();
  closeAllSubModals();
}

// ── FECHAR AO CLICAR FORA ────────────────────────────────
function handleClickOutside(event) {
  if (!showDropdown.value) return;
  if (anchoredScope.contains(event.target)) return;
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false;
    closeAllSubModals();
  }
}

// Watch: Minhas/Outros voltando desmarca Por usuário
watch(
  [() => noteStore.filterIncludeOwn, () => noteStore.filterIncludeComments],
  (newValues, oldValues) => {
    const [newOwn, newComments] = newValues;
    const [oldOwn, oldComments] = oldValues || [];
    if (noteStore.filterIncludeSelectedUsers && ((newOwn && !oldOwn) || (newComments && !oldComments))) {
      noteStore.filterIncludeSelectedUsers = false;
      noteStore.selectedUserIdsForFilter = [];
    }
  }
);

onMounted(() => {
  document.addEventListener("click", handleClickOutside, true);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside, true);
});

// ── COMPUTEDS ────────────────────────────────────────────
const hasPeriodActive = computed(() => {
  const { dateFrom, dateTo } = noteStore.filterPeriod;
  return (dateFrom && dateFrom.trim() !== "") || (dateTo && dateTo.trim() !== "");
});

const activeFilterCount = computed(() => {
  let n = 0;

  // Agora qualquer check visível conta como filtro ativo.
  // Portanto, se esses dois filtros iniciarem marcados por padrão,
  // o contador começará em 2.
  if (noteStore.filterIncludeOwn)             n++;
  if (noteStore.filterIncludeComments)        n++;

  if (obsTypeActive.value)                    n++; // >>> NOVO (tipos ou busca na observação)
  if (noteStore.filterOnlyStandalone)         n++; // >>> NOVO
  if (noteStore.filterIncludeSelectedUsers)   n++;
  if (noteStore.filterIncludeSelectedGroups)  n++;
  // Ordenação só conta quando NÃO é a padrão — sair da ordem do texto é uma
  // escolha do usuário; estar nela não é.
  if (noteStore.filterSortOrder !== DEFAULT_SORT_ORDER) n++;
  if (noteStore.filterByTitle.trim() !== "")  n++;
  if (noteStore.filterByBody.trim() !== "")   n++;
  if (noteStore.filterByTag.trim() !== "")    n++;
  if (noteStore.filterBySource.trim() !== "") n++;
  if (hasPeriodActive.value)                  n++;

  return n;
});

// >>> NOVO: invariante — o NoteFilter nunca pode ficar sem nenhum filtro.
// Se todos os filtros forem desmarcados, "Minhas notas" é remarcada.
// Ordenação (mais antigas) é apenas ordenação, não conta como filtro.
const hasAnyMeaningfulFilter = computed(
  () =>
    noteStore.filterIncludeOwn ||
    noteStore.filterIncludeComments ||
    noteStore.selectedObservationTypes.length > 0 ||
    noteStore.observationSearchTerm.trim() !== "" ||
    noteStore.observationReadState !== "" ||
    noteStore.filterOnlyStandalone ||
    noteStore.filterIncludeSelectedUsers ||
    noteStore.filterIncludeSelectedGroups ||
    noteStore.filterByTitle.trim() !== "" ||
    noteStore.filterByBody.trim() !== "" ||
    noteStore.filterByTag.trim() !== "" ||
    noteStore.filterBySource.trim() !== "" ||
    hasPeriodActive.value
);

// Não força "Minhas notas" enquanto um sub-modal está aberto — o usuário
// pode estar prestes a selecionar usuários/grupos (estado transitório válido).
const anySubModalOpen = computed(
  () =>
    showUserDropdown.value ||
    showGroupDropdown.value ||
    showTitleDropdown.value ||
    showBodyDropdown.value ||
    showTagDropdown.value ||
    showSourceDropdown.value ||
    showPeriodDropdown.value ||
    showObsTypeDropdown.value ||
    showSortDropdown.value
);

watch(
  [hasAnyMeaningfulFilter, anySubModalOpen],
  ([hasFilter, subOpen]) => {
    if (!hasFilter && !subOpen) {
      noteStore.filterIncludeOwn = true;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="relative inline-block" ref="dropdownRef">

    <!-- ══ BOTÃO FILTRAR ══ -->
    <button
      @click.stop="toggleDropdown"
      class="action-btn"
      :class="{ active: showDropdown || activeFilterCount > 0 }"
      type="button"
      aria-label="Filtrar notas"
      title="Filtrar"
    >
      <i class="fas fa-filter"></i>
      <span class="btn-label">Filtrar</span>
      <!-- Sempre no DOM: v-show + opacity evita redimensionamento do botão -->
      <span v-show="activeFilterCount > 0" class="filter-count" aria-hidden="true">•{{ activeFilterCount }}</span>
      <svg class="chev" :class="{ open: showDropdown }" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path clip-rule="evenodd" fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/>
      </svg>
    </button>

    <!-- ══ DROPDOWN ══ -->
    <div v-show="showDropdown" class="row-dropdown-menu row-dropdown-menu--below-end">
      <div class="dropdown-content">

        <!-- ── 1. BUSCA ── -->
        <div class="section-label">Busca</div>

        <!-- Por título — NOVA, primeira posição -->
        <div class="filter-row" :class="{ 'sub-open': showTitleDropdown }" @click="handleTitleClick">
          <i class="fas fa-heading filter-icon"></i>
          <span class="filter-label">
            Por título
            <span v-if="noteStore.filterByTitle.trim() !== ''" class="field-badge field-badge--text">{{ noteStore.filterByTitle }}</span>
          </span>
          <i v-if="noteStore.filterByTitle.trim() !== ''" class="fas fa-check checkmark"></i>
          <SelectFieldFilter
            :is-open="showTitleDropdown"
            label="Buscar no título"
            v-model="noteStore.filterByTitle"
            @apply="applyFieldFilter"
            @close="showTitleDropdown = false"
          />
        </div>

        <div class="filter-row" :class="{ 'sub-open': showBodyDropdown }" @click="handleBodyClick">
          <i class="fas fa-align-left filter-icon"></i>
          <span class="filter-label">
            Por conteúdo
            <span v-if="noteStore.filterByBody.trim() !== ''" class="field-badge field-badge--text">{{ noteStore.filterByBody }}</span>
          </span>
          <i v-if="noteStore.filterByBody.trim() !== ''" class="fas fa-check checkmark"></i>
          <SelectFieldFilter
            :is-open="showBodyDropdown"
            label="Buscar no conteúdo"
            v-model="noteStore.filterByBody"
            @apply="applyFieldFilter"
            @close="showBodyDropdown = false"
          />
        </div>

        <div class="filter-row" :class="{ 'sub-open': showTagDropdown }" @click="handleTagClick">
          <i class="fas fa-hashtag filter-icon"></i>
          <span class="filter-label">
            Por tag
            <span v-if="noteStore.filterByTag.trim() !== ''" class="field-badge field-badge--text">{{ noteStore.filterByTag }}</span>
          </span>
          <i v-if="noteStore.filterByTag.trim() !== ''" class="fas fa-check checkmark"></i>
          <SelectFieldFilter
            :is-open="showTagDropdown"
            label="Buscar por tag"
            v-model="noteStore.filterByTag"
            @apply="applyFieldFilter"
            @close="showTagDropdown = false"
          />
        </div>

        <div class="filter-row" :class="{ 'sub-open': showSourceDropdown }" @click="handleSourceClick">
          <i class="fas fa-link filter-icon"></i>
          <span class="filter-label">
            Por fonte
            <span v-if="noteStore.filterBySource.trim() !== ''" class="field-badge field-badge--text">{{ noteStore.filterBySource }}</span>
          </span>
          <i v-if="noteStore.filterBySource.trim() !== ''" class="fas fa-check checkmark"></i>
          <SelectFieldFilter
            :is-open="showSourceDropdown"
            label="Buscar na fonte"
            v-model="noteStore.filterBySource"
            @apply="applyFieldFilter"
            @close="showSourceDropdown = false"
          />
        </div>

        <div class="dropdown-divider"></div>

        <!-- ── 2. SINALIZAÇÃO ── -->
        <div class="section-label">Sinalização</div>

        <div class="filter-row" @click.stop="toggleIncludeOwn">
          <i class="fas fa-user filter-icon"></i>
          <span class="filter-label">Minhas notas</span>
          <i v-if="noteStore.filterIncludeOwn" class="fas fa-check checkmark"></i>
        </div>

        <div class="filter-row" @click.stop="toggleIncludeComments">
          <i class="fas fa-users filter-icon"></i>
          <span class="filter-label">Notas de outros</span>
          <i v-if="noteStore.filterIncludeComments" class="fas fa-check checkmark"></i>
        </div>

        <!-- >>> NOVO: Tipo de observação (substitui "Com observação") -->
        <!-- Só o dono da atividade filtra por observação (quem faz correções). -->
        <div v-if="isActivityOwner" class="filter-row" :class="{ 'sub-open': showObsTypeDropdown }" @click="handleObsTypeRowClick">
          <i class="fas fa-comment-dots filter-icon"></i>
          <span class="filter-label">
            Observação
            <span v-if="obsCheckedCount > 0" class="field-badge">
              {{ obsCheckedCount }}
            </span>
          </span>
          <i v-if="obsTypeActive" class="fas fa-check checkmark"></i>
          <SelectObservationTypeFilter
            :is-open="showObsTypeDropdown"
            v-model="noteStore.selectedObservationTypes"
            v-model:observation-search="noteStore.observationSearchTerm"
            v-model:read-state="noteStore.observationReadState"
            @change="applyObsTypeFilter"
            @search="applyObsTypeFilter"
            @close="showObsTypeDropdown = false"
          />
        </div>

        <!-- >>> NOVO: Notas avulsas (textRangeStart == 0 && textRangeEnd == 0) -->
        <div class="filter-row" @click.stop="toggleOnlyStandalone">
          <i class="fas fa-unlink filter-icon"></i>
          <span class="filter-label">Notas avulsas</span>
          <i v-if="noteStore.filterOnlyStandalone" class="fas fa-check checkmark"></i>
        </div>

        <div class="dropdown-divider"></div>

        <!-- ── 3. VISIBILIDADE ── -->
        <div class="section-label">Notas Criadas</div>

        <div class="filter-row" :class="{ 'sub-open': showUserDropdown }" @click="handleUserRowClick">
          <i class="fas fa-user-tag filter-icon"></i>
          <span class="filter-label">
            Por usuários
            <span v-if="noteStore.filterIncludeSelectedUsers" class="field-badge">
              {{ noteStore.selectedUserIdsForFilter.length }}
            </span>
          </span>
          <i v-if="noteStore.filterIncludeSelectedUsers" class="fas fa-check checkmark"></i>
          <SelectNoteUser
            :is-open="showUserDropdown"
            @apply-selection="applyUserFilter"
            @close="showUserDropdown = false"
          />
        </div>

        <div class="filter-row" :class="{ 'sub-open': showGroupDropdown }" @click="handleGroupRowClick">
          
          <i class="fas fa-users-cog filter-icon"></i>
          <span class="filter-label">
            Por grupo
            <span v-if="noteStore.filterIncludeSelectedGroups" class="field-badge">
              {{ noteStore.selectedGroupIdsForFilter.length }}
            </span>
          </span>
          <i v-if="noteStore.filterIncludeSelectedGroups" class="fas fa-check checkmark"></i>
          <SelectNoteGroup
            :is-open="showGroupDropdown"
            @apply-selection="applyGroupFilter"
            @close="showGroupDropdown = false"
          />
        </div>

        <div class="dropdown-divider"></div>

        <!-- ── 4. ORDENAÇÃO ── -->
        <div class="section-label">Ordenação</div>

        <!--
          Uma linha que abre mini-modal, no molde de "Filtrar por período". Com
          três ordens exclusivas, uma caixa de marcar deixa de conseguir
          expressá-las — e a ordem vigente passa a ficar escrita na tela.
        -->
        <div
          class="filter-row stacked-row"
          :class="{ 'sub-open': showSortDropdown }"
          @click="handleSortRowClick"
        >
          <i class="fas fa-sort-amount-up filter-icon"></i>
          <span class="filter-label">
            <span class="stacked-label-inner">
              Ordenar notas
              <span class="stacked-chips">
                <span class="field-badge--pill">{{ sortOrderLabel }}</span>
              </span>
            </span>
          </span>
          <i
            v-if="noteStore.filterSortOrder !== 'textOrder'"
            class="fas fa-check checkmark"
          ></i>
          <SelectSortOrderFilter
            :is-open="showSortDropdown"
            v-model="noteStore.filterSortOrder"
            @apply="applySortOrder"
            @close="showSortDropdown = false"
          />
        </div>

        <div class="dropdown-divider"></div>

        <!-- ── 5. PERÍODO ── -->
        <div class="section-label">Período</div>

        <div class="filter-row period-row" :class="{ 'sub-open': showPeriodDropdown }" @click="handlePeriodRowClick">
          <i class="fas fa-calendar-alt filter-icon"></i>
          <span class="filter-label">
            <span class="period-label-inner">
              Filtrar por período
              <!-- Chips separadas: De e Até, cada uma abaixo do rótulo -->
              <span v-if="hasPeriodActive" class="period-chips">
                <span v-if="noteStore.filterPeriod.dateFrom" class="field-badge--date">
                  De {{ noteStore.filterPeriod.dateFrom }}
                </span>
                <span v-if="noteStore.filterPeriod.dateTo" class="field-badge--date">
                  Até {{ noteStore.filterPeriod.dateTo }}
                </span>
              </span>
            </span>
          </span>
          <i v-if="hasPeriodActive" class="fas fa-check checkmark"></i>
          <SelectPeriodFilter
            :is-open="showPeriodDropdown"
            v-model="noteStore.filterPeriod"
            @apply="applyPeriodFilter"
            @close="showPeriodDropdown = false"
          />
        </div>

        <!-- ── LIMPAR FILTROS ── -->
        <template v-if="activeFilterCount > 0">
          <div class="dropdown-divider"></div>
          <div class="clear-row" @click.stop="clearAllFilters">
            <i class="fas fa-times-circle clear-icon"></i>
            <span>Limpar filtros</span>
          </div>
        </template>

      </div>
    </div>

  </div>
</template>

<style scoped>
/* ── Botão ── */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  /* Padding reduzido para compensar o espaço reservado pelo contador */
  padding: 0 0.55rem;
  border-radius: 10px;
  background-color: var(--neutral-200);
  border: 1px solid var(--border-color);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.88rem;
  font-weight: 500;
  line-height: 1;
  /* Sem 'all' — evita animar largura ao revelar o contador */
  transition: background-color 0.15s ease, transform 0.15s ease;
  white-space: nowrap;
}
.action-btn:hover {
  background-color: var(--neutral-300);
}
.action-btn:active { transform: translateY(1px); }
.action-btn.active {
  background-color: var(--neutral-300);
  border-color: transparent;
  font-weight: 700;
}
.btn-label { white-space: nowrap; }
.filter-count {
  font-size: 0.82rem;
  font-weight: 700;
  color: #6b5acd;
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, "SF Mono", "Cascadia Mono", "Roboto Mono", monospace;
  /* Espaço fixo reservado para "•9" (ponto + 1 dígito) — nunca muda */
  min-width: 1.6ch;
  display: inline-block;
  text-align: left;
}
/* v-show seta display:none — sobrescrevemos para manter o espaço reservado */
.filter-count[style*="display: none"] {
  display: inline-block !important;
  opacity: 0;
  pointer-events: none;
}
.chev {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform 0.2s ease-in-out;
}
.chev.open { transform: rotate(180deg); }

.row-dropdown-menu {
  min-width: 230px;
}
.dropdown-content { padding: 2px 0 2px; }

/* ── Seção ── */
.section-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-subtle);
  /* color: #000000; */
  padding: 2px 14px 2px;
  pointer-events: none;
  user-select: none;
}

/* ── Linha de filtro ── */
.filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 14px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.15s, transform 0.05s ease;
  min-height: 30px;
}
.filter-row:hover { background-color: #f5f3ff; }
.filter-row:not(.sub-open):active { transform: translateY(1px); }

/* Linha que cresce verticalmente porque leva um rótulo em cima e o valor em
   baixo. Nasceu no período; a ordenação usa a mesma forma, com nome neutro —
   `.period-row` numa linha de ordenação seria mentira de nomenclatura. */
.period-row,
.stacked-row { align-items: flex-start; padding-top: 7px; padding-bottom: 7px; }
.period-row .filter-icon,
.stacked-row .filter-icon { margin-top: 2px; }
.period-row .checkmark,
.stacked-row .checkmark { margin-top: 2px; }

.filter-icon {
  width: 13px;
  text-align: center;
  font-size: 0.72rem;
  color: var(--text-subtle);
  flex-shrink: 0;
}
.filter-label {
  flex: 1;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--text-dark);
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

/* Rótulo em coluna: nome em cima, valor embaixo */
.period-label-inner,
.stacked-label-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* Valor abaixo do rótulo — datas no período, ordem vigente na ordenação */
.period-chips,
.stacked-chips {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 2px;
}

/* Checkmark roxo */
.checkmark {
  font-size: 0.68rem;
  color: #7c3aed;
  flex-shrink: 0;
}

/* Badges */
.field-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 5px;
  height: 16px;
  font-size: 0.68rem;
  font-weight: 700;
  background-color: #ede9ff;
  color: #6b5acd;
  border-radius: 6px;
  flex-shrink: 0;
}
.field-badge--text {
  /* Chips de busca (título, conteúdo, tag, fonte): truncam se muito longos */
  max-width: 72px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  height: auto;
  padding: 1px 5px;
  line-height: 1.4;
}

/* Chip de largura livre, que exibe o valor por extenso */
.field-badge--date,
.field-badge--pill {
  display: inline-block;
  height: auto;
  padding: 1px 6px;
  line-height: 1.4;
  white-space: nowrap;
  font-size: 0.68rem;
  font-weight: 700;
  background-color: #ede9ff;
  color: #6b5acd;
  border-radius: 6px;
}

/* Limpar */
.clear-row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 14px 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1;
  color: var(--danger-hover);
  transition: background-color 0.15s;
}
.clear-row:hover { background-color: #fff1f2; border-radius: 0 0 11px 11px; }
.clear-row:active > * {
  transform: translateY(1px);
  transition: transform 0.05s ease;
}
.clear-icon {
  font-size: 0.8rem;
  width: 13px;
  text-align: center;
  flex-shrink: 0;
}

.dropdown-divider {
  height: 1px;
  background-color: var(--border-color);
  margin: 4px 0;
}
</style>